import React from 'react';

import { useTranslation } from 'react-i18next';
import { useUser } from '../../../../simple-connect';
import { ToastContext, TopBar, Button, Modal } from '@weezevent/nacre';
import { datadogRum } from '@datadog/browser-rum';
import Storage from './storage';
import Style from '../topBarModules.module.css';
import { fetcher } from '@weezevent/weezjs-core';
import { Loader } from 'semantic-ui-react';

function LoadingCard({ loading = false, children = null, className = '' }) {
    const { t } = useTranslation();
    if (loading) {
        return (
            <div className={className}>
                <Loader style={{ zIndex: '3' }} active>
                    {t('ticket.common.loading')}
                </Loader>
            </div>
        );
    }

    return children;
}

export default function Account({ organization, deleteAccount = false }) {
    const toast = React.useContext(ToastContext);
    const { user } = useUser();
    const { logout, logoutUrl } = useUser();
    const { t } = useTranslation();
    const [openModal, setOpenModal] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    /**
     * @function renderFullName
     * @description render the name of the current user session
     * @return {String} - return "-" or fullname if exists
     */

    const accountsBaseUrl = window.weezConfig.connect.oAuthUrl.replace('auth', '');
    const state = new Storage().initialize();
    const { redirect_uri } = window.weezConfig.connect;

    const items = React.useMemo(() => {
        const components = [
            <TopBar.Account.Item
                key="account"
                primary={{
                    component: 'a',
                    href: `${accountsBaseUrl}settings/account?client_id=${window.weezConfig.connect.clientId}&state=${state}&redirect_uri=${encodeURIComponent(redirect_uri)}`,
                    label: t('ticket.account.manage_profil')
                }}
                mail={user.getEmail()}
                secondary={{
                    style: { color: '#000' },
                    component: 'a',
                    href: logoutUrl,
                    onClick: () => {
                        datadogRum.removeUser();
                        logout();
                    },
                    label: t('ticket.common.action.logout')
                }}
                subtitle={t('ticket.account.card_header')}
                title={user.toString()}
            />
        ];
        if (deleteAccount) {
            components.push(
                <Button key="btn-delete" className={Style['delete-account']} onClick={() => setOpenModal(true)}>
                    {t('ticket.common.action.delete-account')}
                </Button>
            );
        }
        return components;
    }, [openModal, deleteAccount, t]);

    const triggerDeleteAccount = React.useCallback(() => {
        setLoading(true);
        fetcher
            .get(`/ticket/organizations/${organization.id}/delete_account`, {})
            .then(() => {
                // keep loading state to true to prevent user action
                setOpenModal(false);
                toast.success(t('ticket.onboarding.modale_delete.success-delete'));
                setTimeout(() => {
                    datadogRum.removeUser();
                    logout();
                    window.location.href = 'https://weezevent.com/';
                }, 5000);
            })
            .catch(({ response }) => {
                setLoading(false);
                toast.error(t('ticket.onboarding.modale_delete.error-delete'));
                setOpenModal(false);
            });
    }, [organization]);

    return (
        <>
            {deleteAccount && (
                <LoadingCard className={Style['loading-container']} loading={loading}>
                    <Modal
                        title={t('ticket.common.action.delete-account')}
                        open={openModal}
                        onClose={() => setOpenModal(false)}
                        size="extralarge"
                        submitButton={<Button primary label={t('ticket.onboarding.modale_delete.delete_button')} onClick={() => triggerDeleteAccount()} />}
                        cancelButton={<Button label={t('ticket.onboarding.modale_delete.cancel_button')} onClick={() => setOpenModal(false)} />}
                    >
                        {t('ticket.onboarding.modale_delete.modal_content')}
                    </Modal>
                </LoadingCard>
            )}
            <TopBar.Account tooltipTitle={t('ticket.account.tooltip')} content={user.toString()} items={items} />
        </>
    );
}
